import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import SEO from "../../components/SEO";
import { api } from "../../lib/api";
import { hpaDownloadReport, hpaMentorClients, hpaMentorProfile, hpaMentorRelease } from "../../lib/hpaApi";
import { HpaErrorState } from "./components";
import "./hpa.css";
import "./hpa-assessment.css";
import "./hpa-profile.css";

/**
 * SAT HPA™ · Block 11.4 · Mentor Cockpit
 *
 * /mentor/hpa                → Client-Liste (§ 32)
 * /mentor/hpa/{profileId}    → Client-Detail (§ 33–38)
 *
 * Backend bleibt Enforcement (nur zugewiesene Klient:innen).
 */

// ============================================================================
// LIST
// ============================================================================
export function HpaMentorList() {
  const [state, setState] = useState({ loading: true, clients: [], error: null });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await hpaMentorClients();
        if (!alive) return;
        setState({ loading: false, clients: r.clients || [], error: null });
      } catch (_err) {
        if (!alive) return;
        setState({ loading: false, clients: [],
                    error: "Deine Klient:innen-Liste konnte gerade nicht geladen werden." });
      }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <div className="hpa hpa-canvas" data-testid="hpa-mentor-list">
      <SEO title="SAT Mentor · HPA" description="HPA Mentor Cockpit"
            path="/mentor/hpa" />
      <div className="container-sat max-w-5xl pt-24 pb-24 hpa-thread">
        <div className="pl-6 md:pl-10">
          <div className="hpa-overline mb-6">SAT MENTOR · HPA</div>
          <h1 className="hpa-headline text-4xl md:text-5xl mb-6"
              data-testid="hpa-mentor-headline">
            Deine <em>Klient:innen</em>.
          </h1>
          <p className="hpa-lede text-base md:text-lg mb-16 max-w-2xl">
            Nur die Klient:innen, deren HPA dir aktuell zur Prüfung zugewiesen ist.
          </p>

          {state.loading && (
            <div className="text-[color:var(--hpa-text-mute)] text-xs tracking-[0.28em] uppercase">
              Wird geladen…
            </div>
          )}

          {state.error && <HpaErrorState message={state.error} />}

          {!state.loading && !state.error && state.clients.length === 0 && (
            <div className="hpa-card" data-testid="hpa-mentor-empty">
              <div className="hpa-overline mb-4">Aktuell keine Zuweisungen</div>
              <p className="hpa-lede text-base">
                Sobald dir eine HPA-Prüfung zugewiesen wird, erscheint deine
                Klient:in hier.
              </p>
            </div>
          )}

          <div className="flex flex-col gap-4">
            {state.clients.map((c) => (
              <MentorClientRow key={c.profile_id} profileId={c.profile_id} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function MentorClientRow({ profileId }) {
  const [state, setState] = useState({ loading: true, profile: null });
  useEffect(() => {
    let alive = true;
    hpaMentorProfile(profileId)
      .then((p) => alive && setState({ loading: false, profile: p }))
      .catch(() => alive && setState({ loading: false, profile: null }));
    return () => { alive = false; };
  }, [profileId]);

  return (
    <Link
      to={`/mentor/hpa/${profileId}`}
      className="hpa-card block hover:border-[color:var(--hpa-gold-light)] transition-all"
      data-testid={`hpa-mentor-client-${profileId}`}
    >
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <div className="hpa-overline mb-2" style={{ fontSize: "10px" }}>
            HPA · Profil-Version {state.profile?.profile_version ?? "—"}
          </div>
          <div className="hpa-headline text-xl md:text-2xl" style={{ fontWeight: 300 }}>
            Klient:in {profileId.slice(0, 8)}
          </div>
        </div>
        <div className="flex items-center gap-4 text-xs uppercase tracking-[0.24em]">
          <span className="text-[color:var(--hpa-text-soft)]">
            {state.profile?.lifecycle_state || "…"}
          </span>
          <span className="text-[color:var(--hpa-gold)]">
            {state.profile?.review_state === "NONE" ? "Review offen" :
             state.profile?.review_state || "—"}
          </span>
        </div>
      </div>
    </Link>
  );
}

// ============================================================================
// DETAIL
// ============================================================================
export function HpaMentorDetail() {
  const { profileId } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, mentorProfile: null,
                                        clientProfile: null, error: null });
  const [reviewing, setReviewing] = useState(false);
  const [releasing, setReleasing] = useState(false);
  const [notes, setNotes] = useState("");
  const [releaseConfirm, setReleaseConfirm] = useState(false);
  const [actionResult, setActionResult] = useState(null);

  const load = useCallback(async () => {
    setState({ loading: true, mentorProfile: null, clientProfile: null, error: null });
    try {
      const mp = await hpaMentorProfile(profileId);
      let cp = null;
      if (mp.lifecycle_state === "RELEASED") {
        cp = await api.get(`/hpa/profiles/${profileId}`).then((r) => r.data);
      }
      setState({ loading: false, mentorProfile: mp, clientProfile: cp, error: null });
    } catch (err) {
      const code = err?.response?.status;
      setState({
        loading: false, mentorProfile: null, clientProfile: null,
        error: code === 404
          ? "Dieses Profil ist nicht (mehr) verfügbar."
          : code === 403
          ? "Dir ist diese:r Klient:in nicht zugewiesen."
          : "Das Profil konnte gerade nicht geladen werden.",
      });
    }
  }, [profileId]);

  useEffect(() => { load(); }, [load]);

  const doReviewDecision = async (decision, candidateId, reason = "") => {
    setReviewing(true);
    setActionResult(null);
    try {
      const r = await api.post(`/hpa/mentor/profiles/${profileId}/review`, {
        candidate_id: candidateId,
        decision,
        reason,
      }).then((r) => r.data);
      setActionResult({ ok: true,
        msg: `Entscheidung übernommen · ${decision.toLowerCase().replace(/_/g, " ")}` });
      await load();
    } catch (_err) {
      setActionResult({ ok: false, msg: "Die Entscheidung konnte nicht gespeichert werden." });
    } finally {
      setReviewing(false);
    }
  };

  const doRelease = async () => {
    setReleasing(true);
    setActionResult(null);
    try {
      await hpaMentorRelease(profileId, notes);
      setActionResult({ ok: true, msg: "Profil wurde freigegeben." });
      setReleaseConfirm(false);
      await load();
    } catch (err) {
      const code = err?.response?.status;
      setActionResult({
        ok: false,
        msg: code === 409
          ? "Das Profil ist nicht in einem freigebbaren Zustand."
          : "Die Freigabe konnte nicht abgeschlossen werden.",
      });
    } finally {
      setReleasing(false);
    }
  };

  const doDownload = async () => {
    try {
      await hpaDownloadReport(profileId);
    } catch (_e) {
      setActionResult({ ok: false, msg: "Report-Download konnte nicht gestartet werden." });
    }
  };

  return (
    <div className="hpa hpa-canvas" data-testid="hpa-mentor-detail">
      <SEO title="SAT Mentor · HPA Detail" description="HPA Mentor Detail"
            path={`/mentor/hpa/${profileId}`} />
      <div className="container-sat max-w-5xl pt-24 pb-24 hpa-thread">
        <div className="pl-6 md:pl-10">
          <div className="mb-6">
            <button type="button" className="hpa-btn-ghost"
                     onClick={() => navigate("/mentor/hpa")}
                     data-testid="hpa-mentor-back">
              ← Zurück zur Liste
            </button>
          </div>

          {state.loading && (
            <div className="text-[color:var(--hpa-text-mute)] text-xs tracking-[0.28em] uppercase">
              Profil wird geladen…
            </div>
          )}

          {state.error && (
            <HpaErrorState message={state.error} onRetry={load} />
          )}

          {state.mentorProfile && (
            <MentorDetailBody
              mp={state.mentorProfile}
              cp={state.clientProfile}
              onAction={doReviewDecision}
              reviewing={reviewing}
              onOpenRelease={() => setReleaseConfirm(true)}
              onDownload={doDownload}
            />
          )}

          {actionResult && (
            <div className={`mt-8 text-sm ${actionResult.ok ? "text-[color:var(--hpa-gold-light)]" : "text-[color:#E8A6A6]"}`}
                  data-testid="hpa-mentor-action-result">
              {actionResult.msg}
            </div>
          )}

          {releaseConfirm && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
                  data-testid="hpa-mentor-release-modal">
              <div className="hpa-card max-w-lg w-full">
                <div className="hpa-overline mb-3">HPA · Freigabe</div>
                <h3 className="hpa-headline text-2xl mb-4">
                  HPA-Profil für die:den Kunden <em>freigeben</em>?
                </h3>
                <p className="hpa-lede text-[15px] leading-relaxed mb-6">
                  Nach der Freigabe kann die:der Kund:in ihr:sein Profil und den
                  daraus erzeugten Report einsehen. Deine privaten Notizen bleiben
                  ausschließlich in deinem Cockpit sichtbar.
                </p>
                <div className="mb-6">
                  <div className="hpa-overline mb-2">Freigabe-Notiz (optional)</div>
                  <textarea
                    className="w-full min-h-[80px] bg-white/5 rounded-lg p-3 text-[15px]
                                 border border-white/10 focus:border-[color:var(--hpa-gold)]
                                 focus:outline-none text-[color:var(--hpa-text)]"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    maxLength={500}
                    data-testid="hpa-mentor-release-notes"
                  />
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    type="button"
                    className="hpa-btn-primary"
                    disabled={releasing}
                    onClick={doRelease}
                    data-testid="hpa-mentor-release-confirm"
                  >
                    {releasing ? "Wird freigegeben…" : "Freigabe bestätigen"}
                  </button>
                  <button
                    type="button"
                    className="hpa-btn-ghost"
                    onClick={() => setReleaseConfirm(false)}
                    data-testid="hpa-mentor-release-cancel"
                  >
                    Abbrechen
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------
function MentorDetailBody({ mp, cp, onAction, reviewing, onOpenRelease, onDownload }) {
  const isReleased = mp.lifecycle_state === "RELEASED";
  return (
    <>
      {/* Header */}
      <div className="mb-16">
        <div className="hpa-overline mb-4">
          HPA · Profil-Version {mp.profile_version} · {mp.lifecycle_state}
        </div>
        <h1 className="hpa-headline text-3xl md:text-5xl mb-3"
             data-testid="hpa-mentor-detail-headline">
          Klient:in {mp.profile_id.slice(0, 8)}
        </h1>
        <p className="text-[color:var(--hpa-text-soft)] text-sm max-w-xl">
          Owner-Referenz: {mp.owner_user_id.slice(0, 8)} · Review-Status:{" "}
          <span className="text-[color:var(--hpa-gold)]">{mp.review_state}</span>
        </p>
      </div>

      {/* Actions */}
      {!isReleased && (
        <section className="mb-16" data-testid="hpa-mentor-actions">
          <div className="hpa-overline mb-4">Mentor-Entscheidungen</div>
          <p className="hpa-lede text-base leading-relaxed max-w-2xl mb-8">
            Wende Entscheidungen auf einzelne Kandidaten dieser Auswertung an.
            Backend führt die Zustandsprüfung — deine Notizen bleiben privat.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
            <MentorActionButton label="Bestätigen"
              testid="hpa-mentor-action-accept"
              disabled={reviewing}
              onClick={() => onAction("ACCEPT", mp.profile_id)} />
            <MentorActionButton label="Nicht übernehmen"
              testid="hpa-mentor-action-reject"
              disabled={reviewing}
              onClick={() => onAction("REJECT", mp.profile_id)} />
            <MentorActionButton label="Rückfrage stellen"
              testid="hpa-mentor-action-clarification"
              disabled={reviewing}
              onClick={() => onAction("REQUEST_CLARIFICATION", mp.profile_id)} />
            <MentorActionButton label="Als Spannungsfeld bestehen lassen"
              testid="hpa-mentor-action-keep-tension"
              disabled={reviewing}
              onClick={() => onAction("KEEP_AS_TENSION", mp.profile_id)} />
          </div>

          <div className="mt-10">
            <button type="button" className="hpa-btn-primary"
                     onClick={onOpenRelease}
                     data-testid="hpa-mentor-open-release">
              HPA-Profil freigeben
            </button>
          </div>
        </section>
      )}

      {isReleased && (
        <section className="mb-16">
          <div className="hpa-overline mb-4">Report</div>
          <button
            type="button"
            className="hpa-btn-primary"
            onClick={onDownload}
            data-testid="hpa-mentor-download-report"
          >
            HPA-Report herunterladen
          </button>
        </section>
      )}

      {/* Dimension summary */}
      <section className="mb-16" data-testid="hpa-mentor-dimensions">
        <div className="hpa-overline mb-4">Auswertung · Dimensionen</div>
        <h2 className="hpa-headline text-2xl md:text-3xl mb-8">
          Was das Profil <em>sichtbar</em> macht.
        </h2>
        <div className="hpa-dim-accordion">
          {mp.dimensions.map((d) => (
            <details
              key={d.dimension_id}
              className="hpa-dim-row"
              data-testid={`hpa-mentor-dim-${d.dimension_id}`}
            >
              <summary>
                <span className="hpa-dim-row-title">{d.dimension_label}</span>
                <span className="hpa-dim-row-band">{d.display_band}</span>
              </summary>
              <div className="hpa-dim-detail-grid">
                <div>
                  <div className="hpa-overline" style={{ fontSize: "9px" }}>Achse</div>
                  <div>{d.axis_id || "—"}</div>
                </div>
                <div>
                  <div className="hpa-overline" style={{ fontSize: "9px" }}>Konfidenz</div>
                  <div>{(d.confidence * 100).toFixed(0)}%</div>
                </div>
                {d.context_slot && (
                  <div>
                    <div className="hpa-overline" style={{ fontSize: "9px" }}>Kontext</div>
                    <div>{d.context_slot}</div>
                  </div>
                )}
              </div>
            </details>
          ))}
        </div>
      </section>

      {/* Tensions */}
      {mp.tensions && mp.tensions.length > 0 && (
        <section className="mb-16" data-testid="hpa-mentor-tensions">
          <div className="hpa-overline mb-4">Spannungsfelder</div>
          <h2 className="hpa-headline text-2xl md:text-3xl mb-6">
            Was einen zweiten Blick verdient.
          </h2>
          <div className="flex flex-col gap-2">
            {mp.tensions.map((t, i) => (
              <div key={i} className="hpa-tension-card"
                    data-testid={`hpa-mentor-tension-${i}`}>
                <div className="text-[color:var(--hpa-text-soft)] text-sm">{t}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Overall confidence footer */}
      <div className="text-[color:var(--hpa-text-mute)] text-xs uppercase tracking-[0.24em] mt-8">
        Overall Confidence: {(mp.overall_confidence * 100).toFixed(0)}%
      </div>

      <div className="mt-16 pt-6 border-t border-white/5 text-[11px] tracking-[0.24em] uppercase text-[color:var(--hpa-text-mute)]">
        Deine privaten Notizen und Klarstellungsanfragen sind ausschließlich für
        dich sichtbar. Sie erscheinen niemals im Kunden-Report.
      </div>
    </>
  );
}

function MentorActionButton({ label, testid, onClick, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="text-left p-4 rounded-lg border border-white/10 hover:border-[color:var(--hpa-gold-light)]
                 bg-white/[0.02] transition-all disabled:opacity-50"
      data-testid={testid}
    >
      <div className="font-serif text-lg text-[color:var(--hpa-white)] font-light">
        {label}
      </div>
    </button>
  );
}
